/**
 * KaTeX - LAZY, ve SANITIZE EDILMIS DOM uzerinde.
 *
 * ## Neden lazy
 *
 * KaTeX JS ~78 KB gz, CSS ~23 KB, fontlari 18 dosyada ~250 KB woff2. Chunk
 * yalnizca mesaj metni bir matematik siniri iceriyorsa yukleniyor; fontlar da
 * yalnizca gercekten bir glyph render olunca cekiliyor. Sonuc: matematiksiz
 * konusmalar - ezici cogunluk - HICBIR SEY odemiyor.
 *
 * ## Neden sanitize'dan SONRA
 *
 * DOMPurify, KaTeX'in MathML/`annotation` ciktisinin parcalarini SILIYOR;
 * once KaTeX kosarsa render ve erisilebilirlik bozulur. Sonra kostumak
 * GUVENLI cunku KaTeX'in GIRDISI bir LaTeX string'i (HTML degil) ve kendi
 * ciktisini escape ediyor. `throwOnError: false` ve varsayilan `trust: false`
 * korunuyor - `trust: true` `\href` ile `javascript:` URL'i enjekte
 * edilmesine izin verirdi.
 *
 * Bu sira `lib/sanitize.ts` docstring'inde de yazili ve iyi niyetli bir
 * "refactor" davet ediyor; iki yerde de gerekce kayitli olmasi bilincli.
 */

/** Matematik siniri var mi? Chunk'i yuklemeden once ucuz kontrol. */
const MATH_HINT = /\\\[|\\\(/

export function hasMath(text: string): boolean {
  return MATH_HINT.test(text)
}

/** Yuklenen modul; ikinci cagride yeniden import edilmiyor. */
let katexPromise: Promise<typeof import('katex')> | null = null

function loadKatex(): Promise<typeof import('katex')> {
  if (!katexPromise) {
    // CSS de ayni chunk'ta: Vite onu otomatik olarak `<link>` olarak
    // enjekte ediyor ve fontlar ancak bir glyph render olunca cekiliyor.
    katexPromise = Promise.all([import('katex'), import('katex/dist/katex.min.css')]).then(
      ([module]) => module,
    )
  }
  return katexPromise
}

/**
 * Bosta prefetch. Kullanici ilk matematikli yaniti aldiginda chunk hazir
 * olsun; degilse ilk render'da gorunur bir gecikme olur.
 */
export function prefetchKatex(): void {
  const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback
  if (idle) {
    idle(() => void loadKatex())
  } else {
    // Safari'de `requestIdleCallback` yok; kisa bir gecikme yeterli.
    setTimeout(() => void loadKatex(), 1500)
  }
}

// `sanitize.ts` bu isaretleyicileri uretiyor: LaTeX, markdown'dan KORUNMAK
// icin stash'lendi ve sanitize'dan sonra geri yazildi. Simdi onlari gercek
// matematige ceviriyoruz.
const BLOCK = /\\\[([\s\S]+?)\\\]/g
const INLINE = /\\\(([\s\S]+?)\\\)/g

/**
 * Sanitize edilmis bir DOM agacindaki matematigi render eder.
 *
 * Metin dugumleri uzerinde calisiyor, `innerHTML` OKUYUP YAZMIYOR: ikincisi
 * sanitize edilmis agaci yeniden ayristirmak demek olurdu ve bir kacis
 * hatasi tum sanitizasyonu gecersiz kilardi.
 */
export async function renderMathInElement(root: HTMLElement): Promise<void> {
  const text = root.textContent ?? ''
  if (!hasMath(text)) return

  const katex = (await loadKatex()).default

  // `<pre>`/`<code>` ICINDE matematik render EDILMIYOR: kod blogunda
  // `\(` yazan biri formul degil kod yaziyor.
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !hasMath(node.nodeValue)) return NodeFilter.FILTER_REJECT
      const parent = node.parentElement
      if (parent?.closest('pre, code, .katex')) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })

  const targets: Text[] = []
  let current = walker.nextNode()
  while (current) {
    targets.push(current as Text)
    current = walker.nextNode()
  }

  for (const node of targets) {
    replaceMath(node, katex)
  }
}

type Katex = typeof import('katex')['default']

function replaceMath(node: Text, katex: Katex): void {
  const source = node.nodeValue ?? ''
  const fragment = document.createDocumentFragment()
  let cursor = 0

  for (const match of collect(source)) {
    if (match.start > cursor) {
      fragment.append(source.slice(cursor, match.start))
    }
    const span = document.createElement('span')
    try {
      katex.render(match.latex, span, {
        displayMode: match.display,
        // Bozuk LaTeX tum mesaji DUSURMEMELI: hatali formul kirmizi metin
        // olarak gorunuyor ve yanitin geri kalani okunabilir kaliyor.
        throwOnError: false,
        // `trust: false` VARSAYILAN ve korunuyor: `true` olsa `\href` ile
        // `javascript:` URL'i enjekte edilebilirdi.
        output: 'htmlAndMathml',
      })
      fragment.append(span)
    } catch {
      // `throwOnError: false` ile buraya dusulmemeli; yine de metni
      // kaybetmiyoruz.
      fragment.append(source.slice(match.start, match.end))
    }
    cursor = match.end
  }

  if (cursor === 0) return
  if (cursor < source.length) fragment.append(source.slice(cursor))
  node.replaceWith(fragment)
}

interface MathMatch {
  start: number
  end: number
  latex: string
  display: boolean
}

/**
 * Blok ve satir ici formulleri KONUMA GORE sirali toplar.
 *
 * Iki ayri regex'i sirayla uygulamak yanlis olurdu: blok formuller once
 * degistirilirse satir ici olanlarin konumlari kayiyor.
 */
function collect(source: string): MathMatch[] {
  const matches: MathMatch[] = []

  for (const [pattern, display] of [
    [BLOCK, true],
    [INLINE, false],
  ] as const) {
    pattern.lastIndex = 0
    let match = pattern.exec(source)
    while (match) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        latex: match[1],
        display,
      })
      match = pattern.exec(source)
    }
  }

  matches.sort((a, b) => a.start - b.start)

  // Ic ice gecen eslesmeleri at: `\[ ... \( ... \) ... \]` icindeki satir
  // ici eslesme blogun ICINDE kaliyor ve iki kez render edilmemeli.
  const result: MathMatch[] = []
  let lastEnd = 0
  for (const match of matches) {
    if (match.start < lastEnd) continue
    result.push(match)
    lastEnd = match.end
  }
  return result
}
